/**
 * Dual-auth utilities shared across backend, MCP server, and conversations MCP.
 *
 * Supports two token types:
 * - JWT (from WorkOS AuthKit OAuth 2.1) — verified via JWKS
 * - way_ token (scoped API token) — validated via KV lookup
 */

export type TokenType = 'jwt' | 'way_token' | 'unknown';

/**
 * Extract bearer token from an HTTP request's Authorization header.
 */
export function extractBearerToken(request: Request): string | null {
  const authHeader = request.headers.get('Authorization');
  if (!authHeader?.startsWith('Bearer ')) {
    return null;
  }
  return authHeader.slice(7);
}

/**
 * Identify the token type by prefix.
 * - `way_` prefix → scoped API token (KV-backed)
 * - `eyJ` prefix → JWT (base64-encoded JSON header)
 */
export function identifyTokenType(token: string): TokenType {
  if (token.startsWith('way_')) return 'way_token';
  if (token.startsWith('eyJ')) return 'jwt';
  return 'unknown';
}

/**
 * Build a RFC 9728 WWW-Authenticate header for 401 responses.
 *
 * Includes `resource_metadata` so MCP clients can discover
 * the authorization server automatically.
 */
export function buildWwwAuthenticateHeader(
  resourceMetadataUrl: string,
  errorDescription = 'Authorization required',
): string {
  return [
    'Bearer error="unauthorized"',
    `error_description="${errorDescription}"`,
    `resource_metadata="${resourceMetadataUrl}"`,
  ].join(', ');
}
